import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { completeAuth0Login } from "@/auth/auth0";
import { useAuth } from "@/auth/store";
import { api } from "@/lib/api";
import { continueAfterAuthentication } from "@/auth/postAuthDestination";
import type { User } from "@/lib/types";

export const Route = createFileRoute("/callback")({ component: Callback });

function Callback() {
  const setAuth = useAuth((s) => s.setAuth);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard
    ran.current = true;
    (async () => {
      try {
        const auth0Token = await completeAuth0Login();
        // Exchange the Auth0 token for a Ledger Core session token. Direct fetch
        // (not the api client) so we send the Auth0 bearer, not a stale LC token.
        const resp = await fetch("/api/auth/auth0/exchange", {
          method: "POST",
          headers: { Authorization: `Bearer ${auth0Token}` },
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.detail || `Sign-in failed (${resp.status})`);
        }
        const token = (await resp.json()) as { access_token: string };
        localStorage.setItem("ledger.auth.token", token.access_token);
        const me = await api<User>("/api/auth/me");
        setAuth(token.access_token, me);
        continueAfterAuthentication();
      } catch (exc: unknown) {
        setError(exc instanceof Error ? exc.message : "Sign-in failed");
      }
    })();
  }, [setAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-gray-800">
      <div className="w-full max-w-sm bg-white rounded-lg shadow p-8 dark:bg-gray-900 text-center">
        {error ? (
          <>
            <h1 className="text-lg font-semibold text-red-600 mb-2">Sign-in failed</h1>
            <p className="text-sm text-slate-500 mb-4 dark:text-slate-400">{error}</p>
            <a href="/login" className="text-sm text-blue-600 hover:text-blue-700">
              Back to sign-in
            </a>
          </>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">Signing you in…</p>
        )}
      </div>
    </div>
  );
}
